import { memo, useCallback, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import { useFlags } from '@mint-lib/routing-context';

import {
  Box,
  Card,
  Chip,
  Icon,
  StaticDateSpan,
  StaticNumericSpan,
  Tooltip,
  Typography,
} from '@myn/mui';

import {
  useAcceptCo2OrderMutation,
  useRejectCo2OrderMutation,
} from '../../api/cO2Slice.js';
import ConditionalCurrencyField from '../../atoms/ConditionalCurrencyField.jsx';
import { parseDate } from '../../features/Co2NotificationRow/Co2NotificationRow.utils.js';
import { Currency } from '../../types/_campaigns/campaigns.js';
import { Co2Order /*Co2OrderStatus*/ } from '../../types/co2notifications.js';
import NewCo2Modal from '../NewCo2Modal/index.js';
import {
  mapStatusToButtons,
  useMapStatusToDescription,
  useMapStatusToTextMessage,
} from './NewCo2NotificationRow.constants.jsx';

export type Props = {
  item: Co2Order;
  currency: Currency | null;
  isCertificateAcknowledged: boolean;
  onSuccess: (id: number, type: 'confirm' | 'reject') => void;
  openAcknowledgmentModal: (certificateUrl: string) => void;
};

export type Mode = 'confirm' | 'reject';

export const NewCo2NotificationRow = memo(
  ({
    item,
    currency,
    isCertificateAcknowledged,
    onSuccess,
    openAcknowledgmentModal,
  }: Props) => {
    const { t } = useTranslation('@myn/planning');
    const ff = useFlags('@mint/arm', [
      'buyingPlanCarbonOffsetAcknowledgmentDisclaimer',
    ]);

    const mapStatusToDescription = useMapStatusToDescription(t);
    const mapStatusToTextMessage = useMapStatusToTextMessage(t);

    const [modalMode, setModalMode] = useState<Mode | null>(null);

    const isUserReadOnly = !useIsAllowedPermission(
      'campaignPlanningFeature:modify',
    );
    const co2ReadOnly = !useIsAllowedPermission('carbonNeutralFeature:modify');
    const [acceptCo2Order] = useAcceptCo2OrderMutation();
    const [rejectCo2Order] = useRejectCo2OrderMutation();
    const {
      id,
      dateFrom,
      dateTo,
      orderStatus,
      campaignCollectionName,
      mediaPlanName,
      isCurrencyConverted,
      emissions,
      offsetCost,
      verifyUrl,
    } = item;

    const parsedDateFrom = parseDate(dateFrom);
    const parsedDateTo = parseDate(dateTo);
    const dateRange = (
      <Box>
        <StaticDateSpan date={parsedDateFrom} />
        -
        <StaticDateSpan date={parsedDateTo} />
      </Box>
    );
    const Co2Offset = (
      <Box>
        <StaticNumericSpan
          number={emissions}
          options={{
            suffix: ' Kg',
            minDecimals: 2,
          }}
        />
      </Box>
    );

    const handleConfirm = () => setModalMode('confirm');
    const handleClose = useCallback(() => setModalMode(null), [setModalMode]);
    const handleSave = useCallback(async () => {
      if (modalMode === 'confirm') {
        try {
          await acceptCo2Order({ id }).unwrap();
          onSuccess(id, modalMode);
        } catch {
          enqueueSnackbar({
            variant: 'error',
            autoHideDuration: 3000,
            subTitle: `It can not be accepted.`,
            title: 'Error',
            textLayout: 'inline',
            removable: true,
          });
        }
      }

      if (modalMode === 'reject') {
        try {
          await rejectCo2Order({ id }).unwrap();
          onSuccess(id, modalMode);
        } catch {
          enqueueSnackbar({
            variant: 'error',
            autoHideDuration: 3000,
            subTitle: `It can not be rejected.`,
            title: 'Error',
            textLayout: 'inline',
            removable: true,
          });
        }
      }

      setModalMode(null);
    }, [modalMode, id, onSuccess, acceptCo2Order, rejectCo2Order]);
    const handleReject = () => setModalMode('reject');

    const handleWebsiteButtonClick = () => {
      if (
        ff.buyingPlanCarbonOffsetAcknowledgmentDisclaimer.enabled &&
        !isCertificateAcknowledged
      ) {
        openAcknowledgmentModal(verifyUrl);
        return;
      }

      globalThis.open(verifyUrl, '_blank');
    };

    const shouldDisplayTopButtons =
      orderStatus === 'booked' ||
      orderStatus === 'rejected' ||
      orderStatus === 'draft';

    const shouldDisplayBottomButtons =
      orderStatus === 'booked' || orderStatus === 'invoiced';

    const isTopButtons = orderStatus !== 'draft';

    const statusButtonOptions = {
      item,
      status: orderStatus,
      onAccept: handleConfirm,
      onReject: handleReject,
      onWebsiteButtonClick: handleWebsiteButtonClick,
      readOnlyUser: isUserReadOnly,
      co2ReadOnly,
      isCurrencyConverted,
      isCertificateAcknowledged,
      isAcknowledgedCertificateFeatureEnabled:
        ff.buyingPlanCarbonOffsetAcknowledgmentDisclaimer.enabled,
    };

    const topStatusButtons = mapStatusToButtons(t)[orderStatus]({
      ...statusButtonOptions,
      isTopButtons,
    });

    const bottomStatusButtons = mapStatusToButtons(t)[orderStatus]({
      ...statusButtonOptions,
    });

    return (
      <Card
        variant="outlined"
        sx={{
          padding: (theme) => theme.spacing(1.5),
          marginBottom: (theme) => theme.spacing(2),
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          paddingBottom={(theme) => theme.spacing(2)}
        >
          <Box display="flex" gap={0.4}>
            <Typography
              variant="h4"
              data-for={`co2-item-campaign-name-${id}`}
              data-tip={campaignCollectionName}
            >
              {campaignCollectionName}
            </Typography>
            <Typography variant="h4">-</Typography>
            <Tooltip title={mediaPlanName}>
              <Typography variant="h4">{mediaPlanName}</Typography>
            </Tooltip>
          </Box>
          {shouldDisplayTopButtons ? topStatusButtons : null}
        </Box>
        <Typography>{mapStatusToDescription[orderStatus]}</Typography>
        <Typography>{mapStatusToTextMessage[orderStatus]}</Typography>
        <Box
          paddingTop={(theme) => theme.spacing(2)}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box display="flex" gap={(theme) => theme.spacing(1)}>
            <Chip
              variant="outlined"
              icon={<Icon component="Calendar" />}
              label={dateRange}
            />
            <Chip
              variant="outlined"
              icon={<Icon component="Weight" />}
              label={Co2Offset}
            />
            <Chip
              variant="outlined"
              icon={<Icon component="Leaf" />}
              label={
                <ConditionalCurrencyField
                  currency={currency}
                  value={offsetCost}
                  decimals={2}
                />
              }
            />
          </Box>
          {shouldDisplayBottomButtons ? bottomStatusButtons : null}
        </Box>
        <NewCo2Modal
          onSave={handleSave}
          onClose={handleClose}
          co2ToOffset={emissions}
          offsetCost={offsetCost}
          currency={currency}
          mode={modalMode}
        />
      </Card>
    );
  },
);
